'use client';

import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useCallback } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  AccordionContent,
  Accordion,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion.component';
import { Link } from '@/components/ui/link/link.component';
import { shouldHideNav } from '@/utils/navbar.util';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { HeaderNavItem } from '@/app/(main)/_components/sign-in-modal/_types/header.types';

interface navbarSheetProps {
  navItems: HeaderNavItem[];
}

export function NavbarSheet({ navItems }: navbarSheetProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  if (shouldHideNav(pathname)) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          className="lg:hidden"
          aria-label="Abrir menu"
          data-testid={DataTestIdEnum.HEADER_HAMBURGER_BUTTON}
        >
          <Icon
            icon={faBars}
            className="text-gray-700 ml-[-0.3125rem]"
            size="lg"
          />
        </button>
      </SheetTrigger>
      <SheetContent
        aria-label="Menu"
        aria-labelledby={undefined}
        title="Menu"
        side="left"
        hasHeader
        className="w-full sm:w-[25rem] p-0 overflow-y-auto"
        data-testid={DataTestIdEnum.HEADER_MOBILE_MENU}
      >
        <div className="flex px-5 pt-4">
          <SheetClose asChild>
            <ModalCloseButton skipInitialFocus />
          </SheetClose>
        </div>
        <Accordion
          type="multiple"
          className="mt-4"
          defaultValue={navItems.map((navbarItem) => navbarItem.normalLabel)}
        >
          <nav>
            {navItems.map((navbarItem) => (
              <AccordionItem
                value={navbarItem.normalLabel}
                key={navbarItem.normalLabel}
              >
                <AccordionTrigger
                  showNoIcon
                  underlineOnHover={false}
                  className="group gap-2 px-6 py-4 text-lg font-normal text-gray-800 [@media(hover:hover)]:hover:bg-gray-100"
                  data-testid={navbarItem.dataTestId}
                >
                  <Icon
                    icon={navbarItem.icon}
                    className="w-5 h-4 shrink-0 text-gray-800"
                  />
                  <span className="flex-1 text-left">
                    {navbarItem.normalLabel}
                  </span>
                  <Icon
                    icon={faAngleDown}
                    className="w-5 h-4 shrink-0 text-gray-800 transition-transform duration-200 group-data-[state=open]:rotate-180"
                  />
                </AccordionTrigger>
                <AccordionContent className="!text-left !justify-start">
                  <ul className="w-full">
                    {navbarItem.subItems.map((subItem) => (
                      <li key={subItem.label}>
                        {subItem.url
                          ? (
                              <Link
                                prefetch={false}
                                href={subItem.url}
                                className="py-4 flex w-full !justify-start !place-content-start pl-[3.25rem] pr-6 text-gray-800 font-normal text-lg [@media(hover:hover)]:hover:bg-gray-100 focus-visible:bg-gray-50 focus-visible:text-success-700 transition-colors duration-300"
                                data-testid={subItem.dataTestId}
                                data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
                                data-analytics-properties={subItem['data-analytics-properties']}
                                target={subItem.target}
                                onClick={close}
                              >
                                {subItem.label}
                              </Link>
                            )
                          : (
                              <button
                                onClick={subItem.onClick}
                                className="py-4 flex w-full !justify-start !place-content-start pl-[3.25rem] pr-6 text-gray-800 font-normal text-lg [@media(hover:hover)]:hover:bg-gray-100 focus-visible:bg-gray-50 focus-visible:text-success-700 transition-colors duration-300"
                                data-testid={subItem.dataTestId}
                                data-analytics-event={AnalyticsEnum.CLICK_HEADER_NAV_ITEM}
                                data-analytics-properties={subItem['data-analytics-properties']}
                              >
                                {subItem.label}
                              </button>
                            )}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </nav>
        </Accordion>
      </SheetContent>
    </Sheet>
  );
}
